/**
 * Typy i wywołania API panelu administracyjnego (`/api/admin`).
 * JSON zostaje w snake_case — tak jak w całym kontrakcie (CONTRACTS.md §3).
 */

import { api } from '@/lib/api'
import type { Paginated, PermissionDecision, PermissionScope } from '@/lib/types'

export type Decision = PermissionDecision
export type Scope = PermissionScope

/* ------------------------------------------------------------------ role */

export interface AdminRole {
  id: number
  name: string
  description: string | null
  user_count: number
}

export interface PermissionRow {
  key: string
  label: string
  /** Podtytuł wiersza („Zakres widocznych spraw"). */
  hint: string
  group: string
  scoped: boolean
  /** `false` → segment „Warunkowo" jest niedostępny (uprawnienia admin.*). */
  conditional_allowed: boolean
  /** `null` = brak wiersza w macierzy (żaden segment nie jest aktywny). */
  decision: Decision | null
  scope: Scope | null
}

export interface PermissionGroup {
  group: string
  permissions: PermissionRow[]
}

export interface RolePermissions {
  role: AdminRole
  groups: PermissionGroup[]
}

export interface PermissionUpdate {
  key: string
  decision: Decision | null
  scope?: Scope | null
}

/* ------------------------------------------------------------ użytkownicy */

export interface RoleRef {
  id: number
  name: string
}

export interface AdminUser {
  id: number
  email: string
  first_name: string
  last_name: string
  full_name: string
  initials: string
  title: string | null
  is_active: boolean
  roles: RoleRef[]
}

export interface UserWithPassword {
  user: AdminUser
  temporary_password: string | null
}

export interface UserCreateBody {
  email: string
  first_name: string
  last_name: string
  title?: string | null
  role_ids: number[]
}

export interface UserPatchBody {
  first_name?: string
  last_name?: string
  title?: string | null
  is_active?: boolean
  role_ids?: number[]
}

export interface UsersQuery {
  q?: string
  role_id?: number
  page?: number
  size?: number
}

/* ---------------------------------------------------------------- moduły */

export interface ModuleVisibility {
  role_id: number
  role_name: string
  visible: boolean
  /** Checkbox zablokowany (np. moduł „admin" dla roli z uprawnieniami admin.*). */
  locked: boolean
}

export interface AdminModule {
  key: string
  name: string
  description: string
  core: boolean
  /** Stan z MODULES_ENABLED — TYLKO DO ODCZYTU (CONTRACTS.md §0.2). */
  enabled: boolean
  loaded: boolean
  visibility: ModuleVisibility[]
}

/* ------------------------------------------------------------------ API */

export const adminApi = {
  roles: () => api.get<AdminRole[]>('/admin/roles'),
  createRole: (body: { name: string; description?: string | null }) =>
    api.post<AdminRole>('/admin/roles', body),
  duplicateRole: (roleId: number, body: { name?: string | null } = {}) =>
    api.post<AdminRole>(`/admin/roles/${roleId}/duplicate`, body),
  permissions: (roleId: number) => api.get<RolePermissions>(`/admin/roles/${roleId}/permissions`),
  savePermissions: (roleId: number, permissions: PermissionUpdate[]) =>
    api.put<RolePermissions>(`/admin/roles/${roleId}/permissions`, { permissions }),

  users: (query: UsersQuery) =>
    api.get<Paginated<AdminUser>>('/admin/users', {
      query: { q: query.q, role_id: query.role_id, page: query.page, size: query.size },
    }),
  createUser: (body: UserCreateBody) => api.post<UserWithPassword>('/admin/users', body),
  patchUser: (userId: number, body: UserPatchBody) =>
    api.patch<AdminUser>(`/admin/users/${userId}`, body),
  resetPassword: (userId: number) =>
    api.post<UserWithPassword>(`/admin/users/${userId}/reset-password`),

  modules: () => api.get<AdminModule[]>('/admin/modules'),
  saveVisibility: (moduleKey: string, visibility: { role_id: number; visible: boolean }[]) =>
    api.put<AdminModule>(`/admin/modules/${moduleKey}/visibility`, { visibility }),
}

/** Klucze zapytań TanStack Query dla modułu admin. */
export const adminKeys = {
  roles: ['admin', 'roles'] as const,
  permissions: (roleId: number) => ['admin', 'roles', roleId, 'permissions'] as const,
  users: (query: UsersQuery) => ['admin', 'users', query] as const,
  modules: ['admin', 'modules'] as const,
}
